/* eslint-disable */
import React from 'react';
import styled from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import { Text, ProgressIndicator, Layout } from '@oneaudi/unified-web-components';
import { SYS_SPACE_FIXED_400 } from '@oneaudi/unified-web-common';
import SearchResultsList from '../SearchResultsList/SearchResultsList';
import { messages } from '../../../i18n/messages';
import mapContext from '../../../contexts/map-context';
import type { Dealer } from '../../../models/dealer';
import { SearchResultsAmount } from './SearchResultsAmount';
import type { UnifiedTheme } from '../../../FeatureApp';
import { DISPLAY_SIZE } from '../../common/global.style.constants';

const device = {
  desktop: `(min-width:${DISPLAY_SIZE.DESKTOP})`,
  largeDesktop: `(min-width:${DISPLAY_SIZE.LARGE_DESKTOP})`,
  smallDesktop: `(min-width:${DISPLAY_SIZE.SMALL_DESKTOP})`,
  mobile: `(max-width:${DISPLAY_SIZE.MOBILE})`,
  tablet: `(min-width: ${DISPLAY_SIZE.TABLET})`,
  smallTablet: `(max-width: ${DISPLAY_SIZE.TABLET})`,
};

type Props = {
  results: Dealer[] | null;
};

export const SearchResultsGroup = React.forwardRef<HTMLDivElement, Props>(({ results }, ref) => {
  const { isLoading, oneHeader } = mapContext.useMapState();
  const resultEmptyText = useI18n(messages.search_result_empty);

  if (!results) {
    return (
      <SearchResultsGroupContainer
        data-test-id="search-results-group"
        ref={ref}
        direction="column"
        margin={['24px', '28px', '24px', '28px']}
      />
    );
  }

  return isLoading ? (
    <LoaderContainer>
      <ProgressIndicator variant="bar" type="infinite" data-test-id="loader-component" />
    </LoaderContainer>
  ) : (
    <SearchResultsGroupContainer
      data-test-id="search-results-group"
      ref={ref}
      direction="column"
      oneHeader={oneHeader}
    >
      {results && results.length === 0 ? (
        <SearchResultsEmptyContainer data-test-id="search-results-empty-container">
          <Text tag="span" variant={{ order: '1', style: 'normal', type: 'copy' }}>
            {resultEmptyText}
          </Text>
        </SearchResultsEmptyContainer>
      ) : (
        <>
          <SearchResultsAmountContainer>
            <SearchResultsAmount />
          </SearchResultsAmountContainer>
          <SearchResultsList results={results} />
        </>
      )}
    </SearchResultsGroupContainer>
  );
});

const SearchResultsEmptyContainer = styled.div`
  margin: 24px;
`;

const SearchResultsGroupContainer = styled(Layout)<{ oneHeader?: boolean }>`
  position: relative;
  height: 100%;
  max-height: calc(542px - 48px - 32px);
  overflow-y: auto;
  overflow-x: hidden;
  background-color: ${(props: UnifiedTheme) =>
    props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
  padding: 0 28px 24px 28px;

  @media ${device.smallDesktop} {
    max-height: ${({ oneHeader }) => (oneHeader ? 'calc(542px - 48px - 32px)' : '100%')};
  }

  &:hover {
    &::-webkit-scrollbar-thumb {
      visibility: visible;
    }
  }

  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
    visibility: hidden;
  }
`;

const LoaderContainer = styled.div`
  padding: 24px;
`;
const SearchResultsAmountContainer = styled.div`
  position: sticky;
  top: -1;
  background: ${(props: UnifiedTheme) =>
    props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
  margin: 0 0 ${SYS_SPACE_FIXED_400};

  @media ${device.smallDesktop} {
    margin: ${SYS_SPACE_FIXED_400} 0;
  }
`;
